import { useState } from 'react'; // 롤백 시: import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MainColor, SubColor } from "../../style/styleValue";
import { useLang } from "../../i18n";

const Hero = () => {
  const navigate = useNavigate();
  const lang = useLang();
  // [고정] 두 번째 이미지(hero2.png)로 고정 요청. 롤백 시 아래 주석 처리된 원본 로직 복구.
  const [currentSlide /*, setCurrentSlide */] = useState(1);
  // const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 0,
      bg: '/img/background.jpg',
      showCard: true
    },
    {
      id: 1,
      bg: '/img/hero2.png',
      showCard: false
    }
  ];

  // [고정] 배경 이미지 자동 전환 비활성화. 롤백 시 아래 주석 복구 + 위 setCurrentSlide 복구.
  // useEffect(() => {
  //   const slideInterval = setInterval(() => {
  //     setCurrentSlide((prev) => (prev + 1) % slides.length);
  //   }, 4000);

  //   return () => clearInterval(slideInterval);
  // }, [slides.length]);

  const activeSlide = slides[currentSlide];

  return (
    <HeroWrapper>
      {slides.map((slide, index) => (
        <BackgroundLayer 
          key={slide.id}
          $bgImg={slide.bg}
          $isActive={index === currentSlide}
        />
      ))}

      <GradientOverlay />

      <div className="container">
        <div className="hero-flex">
          <TextContent>
            {lang === 'en' ? (
              <>
                <Title>
                  A child's future begins<br />
                  with an accurate diagnosis
                </Title>
                <SubTitle>
                  Connecting diagnosis, education and research<br />
                  <strong>Pediatric Pathology Atlas</strong>
                </SubTitle>
              </>
            ) : (
              <>
                <Title>
                  정확한 진단에서<br />
                  아이의 미래가 시작됩니다
                </Title>
                <SubTitle>
                  진단 · 교육 · 연구를 연결하는<br />
                  <strong>소아병리아틀라스</strong>
                </SubTitle>
              </>
            )}
            
            <ButtonGroup>
              <ActionButton onClick={() => navigate('/portal')}>
                Browse Diseases
              </ActionButton>
              <SecondaryButton onClick={() => navigate('/gallery')}>
                View Atlas Gallery <span>&gt;</span>
              </SecondaryButton>
            </ButtonGroup>
          </TextContent>

          <ImageContent $isVisible={activeSlide.showCard}>
            <div className="image-card">
              <img src="/img/hero_card.png" alt="Pathology Atlas Slides" />
            </div>
          </ImageContent>
        </div>
      </div>
    </HeroWrapper>
  );
};

const HeroWrapper = styled.section`
  position: relative;
  width: 100%;
  height: calc(100vh - 60px);
  min-height: 560px;
  display: flex;
  align-items: center;
  overflow: hidden;
  background-color: #fff;

  .container {
    position: relative;
    z-index: 2;
    /* max-width: 1800px; */
    margin: 0 auto;
    width: 100%;
    padding: 0 90px;
  }

  .hero-flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 120px;
  }

  @media (max-width: 768px) {
    height: auto;
    padding: 80px 0;
    .hero-flex {
      flex-direction: column;
      text-align: center;
    }
  }
`;

interface BackgroundLayerProps {
  $bgImg: string;
  $isActive: boolean;
}

const BackgroundLayer = styled.div<BackgroundLayerProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.$bgImg});
  background-size: cover;
  background-position: center;

  opacity: ${props => (props.$isActive ? 1 : 0)};
  transition: opacity 1.5s ease-in-out, transform 6s ease-out;
  z-index: 0;
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to left,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0.7) 50%,
    rgba(255, 255, 255, 0.95) 95%
  );
  z-index: 1;
`;

const TextContent = styled.div`
  flex: 1;
  z-index: 3;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 2.8rem);
  line-height: 1.3;
  color: #000;
  margin-bottom: 25px;
  letter-spacing: -1px;
`;

const SubTitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 40px;

  strong {
    color: #000;
    font-size: 1.8rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ActionButton = styled.button`
  background-color: ${SubColor};
  color: white;
  padding: 14px 28px;
  border-radius: 6px;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${MainColor};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(44, 90, 160, 0.3);
  }
`;

const SecondaryButton = styled.button`
  background-color: white;
  color: #555;
  padding: 14px 28px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  span {
    margin-left: 10px;
    font-size: 0.9rem;
    color: #ccc;
  }

  &:hover {
    background-color: #f9f9f9;
    border-color: #bbb;
    span { color: #888; }
  }
`;

interface ImageContentProps {
  $isVisible: boolean;
}

const ImageContent = styled.div<ImageContentProps>`
  flex: 1.5;
  display: flex;
  justify-content: flex-end;
  z-index: 3;
  
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transform: translateY(${props => (props.$isVisible ? '0' : '20px')});
  transition: opacity 1.2s ease-in-out, transform 1.2s ease-in-out;
  pointer-events: ${props => (props.$isVisible ? 'auto' : 'none')};

  .image-card {
    img {
      display: block;
      max-width: 100%;
      height: auto;
      border-radius: 4px;
    }
  }

  @media (max-width: 768px) {
    justify-content: center;
    width: 80%;
  }
`;

export default Hero;